import { graphql, navigate, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { ReactComponent as Logo } from '../../../images/fs-2024-logo-black.svg';
import { PrimaryLink } from '../../Common/Links';
import { NotificationBar } from '../../Common/NotificationBar';
import SmartLink from '../../Common/SmartLink';
import ContentContainer from '../../PageSections/ContentContainer';
import * as styles from './Header.module.scss';
import Navigation from './Navigation/Navigation';

const Header = ({
  isBlog = false,
  displayCta = true,
  onCtaClick = null,
  hideNav = false,
  className = '',
  pageMeta = null,
}) => {
  const data = useStaticQuery(graphql`
    query {
      settings: contentfulSiteSettings(contentful_id: { eq: "3vxKUCou9H8hWkqL5dcaNm" }) {
        notificationBarLabel
        notificationBarUrl
        notificationBarDisplayedAllPages
        overrideIndividualPageNotifications
        notificationBarColor
        callsToAction {
          title
          url
          isNoFollow
          animation
        }
      }
    }
  `);

  const { settings } = data;
  const firstCta = settings.callsToAction[0];
  const secondCta = settings.callsToAction[1];
  const [showSlide, setShowSlide] = useState(false);

  const ctaUrl = secondCta ? secondCta.url : firstCta.url;

  const handleSlide = async () => {
    await setShowSlide(true);
  };

  const sliderAnimation = () => {
    if (showSlide) {
      navigate(ctaUrl);
    }
  };

  const ctaButton = (
    <li className="main-cta-obs">
      <PrimaryLink
        slideIn={false}
        url={ctaUrl}
        isButton
        label={secondCta ? secondCta.title : firstCta.title}
        className={styles.button}
        handleSlide={handleSlide}
      />
      <div
        onAnimationEnd={sliderAnimation}
        id="slider"
        className={[styles.slide, showSlide ? styles.showSlide : styles.hideSlide].join(' ')}
      ></div>
    </li>
  );

  const ctaLink = (
    <li>
      <SmartLink to={firstCta.url}>{firstCta.title}</SmartLink>
    </li>
  );

  return (
    <>
      <div className={styles.sticky}>
        <ContentContainer className={className}>
          <header className={styles.siteHeader} data-analytics-section={'site-header-nav'}>
            <nav className={styles.siteNav}>
              <div role="banner" className={styles.logo}>
                <SmartLink to="/">
                  <Logo aria-label="Fullstory Logo" role="img" />
                </SmartLink>
                {isBlog && (
                  <div className={styles.blogTitle}>
                    <SmartLink to="/blog/">BLOG</SmartLink>
                  </div>
                )}
              </div>
              {!hideNav && (
                <>
                  <Navigation />
                  <ul className={[styles.ctaNav, 'unstyled-list'].join(' ')}>
                    {secondCta && ctaLink}
                    {displayCta && ctaButton}
                  </ul>
                </>
              )}
              {hideNav && displayCta && (
                <ul className={[styles.ctaNav, 'unstyled-list'].join(' ')}>
                  <li>
                    <PrimaryLink
                      isButton
                      onClick={onCtaClick}
                      label={secondCta ? secondCta.title : firstCta.title}
                      className={styles.button}
                    />
                  </li>
                </ul>
              )}
            </nav>
          </header>
        </ContentContainer>
      </div>
      {pageMeta && <NotificationBar meta={pageMeta} settings={settings} />}
    </>
  );
};

Header.propTypes = {
  isBlog: PropTypes.bool,
  displayCta: PropTypes.bool,
  onCtaClick: PropTypes.func,
  hideNav: PropTypes.bool,
  dark: PropTypes.bool,
  className: PropTypes.string,
  pageMeta: PropTypes.object,
};

export default Header;
